import { useState } from "react";
import { regions, years, seasonOptions } from "@/mocks/media";

export interface CategoryFilterState {
  genre: string;
  year: number | 0;
  region: string;
  season: number | 0;
  sort: "updated" | "heat" | "rating";
}

interface FilterPanelProps {
  genres: string[];
  showSeason: boolean;
  value: CategoryFilterState;
  onChange: (next: CategoryFilterState) => void;
  onReset: () => void;
}

const sortOptions: { key: CategoryFilterState["sort"]; label: string }[] = [
  { key: "updated", label: "按更新时间" },
  { key: "heat", label: "按热度" },
  { key: "rating", label: "按评分" },
];

export default function FilterPanel({ genres, showSeason, value, onChange, onReset }: FilterPanelProps) {
  const [open, setOpen] = useState(false);

  const set = (patch: Partial<CategoryFilterState>) => onChange({ ...value, ...patch });

  const chip = (active: boolean) =>
    `whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors md:text-sm ${
      active
        ? "border-primary-500 bg-primary-500 font-500 text-background-950"
        : "border-background-300 text-foreground-300 hover:border-primary-500 hover:text-primary-400"
    }`;

  return (
    <div className="rounded-lg border border-background-200 bg-background-100">
      <div className="flex items-center justify-between gap-3 p-4 md:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 items-center gap-2 rounded-md bg-background-200 px-4 text-sm font-500 text-foreground-950"
        >
          <i className="ri-filter-3-line text-base" aria-hidden="true"></i>
          筛选与排序
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true"></i>
        </button>
        <button
          type="button"
          onClick={onReset}
          className="flex h-10 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-300"
        >
          <i className="ri-refresh-line text-base" aria-hidden="true"></i>
          重置
        </button>
      </div>

      <div className={`${open ? "block" : "hidden"} px-4 pb-4 md:block md:p-5`}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-start">
            <span className="w-16 shrink-0 pt-1 text-sm text-foreground-500">类型</span>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={chip(value.genre === "")} onClick={() => set({ genre: "" })}>
                全部
              </button>
              {genres.map((g) => (
                <button key={g} type="button" className={chip(value.genre === g)} onClick={() => set({ genre: g })}>
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 md:flex-row md:items-start">
            <span className="w-16 shrink-0 pt-1 text-sm text-foreground-500">年份</span>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={chip(value.year === 0)} onClick={() => set({ year: 0 })}>
                全部
              </button>
              {years.map((y) => (
                <button key={y} type="button" className={chip(value.year === y)} onClick={() => set({ year: y })}>
                  {y}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 md:flex-row md:items-start">
            <span className="w-16 shrink-0 pt-1 text-sm text-foreground-500">地区</span>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={chip(value.region === "")} onClick={() => set({ region: "" })}>
                全部
              </button>
              {regions.map((r) => (
                <button key={r} type="button" className={chip(value.region === r)} onClick={() => set({ region: r })}>
                  {r}
                </button>
              ))}
            </div>
          </div>

          {showSeason && (
            <div className="flex flex-col gap-2 md:flex-row md:items-start">
              <span className="w-16 shrink-0 pt-1 text-sm text-foreground-500">季数</span>
              <div className="flex flex-wrap gap-2">
                <button type="button" className={chip(value.season === 0)} onClick={() => set({ season: 0 })}>
                  全部
                </button>
                {seasonOptions.map((s) => (
                  <button key={s} type="button" className={chip(value.season === s)} onClick={() => set({ season: s })}>
                    第 {s} 季
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 border-t border-background-200 pt-4 md:flex-row md:items-center">
            <span className="w-16 shrink-0 text-sm text-foreground-500">排序</span>
            <div className="flex flex-wrap gap-2">
              {sortOptions.map((s) => (
                <button key={s.key} type="button" className={chip(value.sort === s.key)} onClick={() => set({ sort: s.key })}>
                  {s.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={onReset}
              className="ml-auto hidden h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-300 transition-colors hover:border-primary-500 hover:text-primary-400 md:flex"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true"></i>
              重置筛选
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}